import {
  ArrowRight,
  Check,
  ChevronRight,
  CircleArrowOutUpRight,
  Database,
  FileSearch,
  Fingerprint,
  LockKeyhole,
  Menu,
  Network,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { FormEvent, useState } from "react";
import logo from "./assets/light-mode-logo.png";

const demoUrl = "https://demo.usevox.io";
const DEFAULT_SIGNUP_WORKER_URL = "https://signup.usevox.io";

async function submitToWorker(email: string) {
  const workerUrl = import.meta.env.VITE_SIGNUP_WORKER_URL?.trim() || DEFAULT_SIGNUP_WORKER_URL;

  const response = await fetch(workerUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(errorText || "Submission failed");
  }

  return true;
}

function Wordmark() {
  return (
    <a className="wordmark" href="#top" aria-label="Vox home">
      <img className="wordmark-logo" src={logo} alt="Vox logo" />
      <span>vox</span>
    </a>
  );
}

export function MailingListForm({ compact = false }: { compact?: boolean }) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const workerUrl = import.meta.env.VITE_SIGNUP_WORKER_URL?.trim() || DEFAULT_SIGNUP_WORKER_URL;
  const formAction = import.meta.env.VITE_MAILING_LIST_ACTION?.trim();
  const hasWorkerConfig = true;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      event.preventDefault();
      setMessage("Please provide a work email address.");
      return;
    }

    if (hasWorkerConfig) {
      event.preventDefault();
      setMessage("Saving your email...");

      try {
        await submitToWorker(trimmedEmail);
        setEmail("");
        setMessage("Thanks — you’re on the list.");
      } catch (error) {
        setMessage(
          error instanceof Error
            ? error.message
            : "We couldn't save your email right now. Please try again in a moment.",
        );
      }

      return;
    }

    if (formAction) {
      return;
    }

    event.preventDefault();
    setMessage("Signup is not configured. Please try again later.");
  }

  return (
    <div className={compact ? "form-wrap form-wrap-compact" : "form-wrap"}>
      <form
        className="mailing-form"
        action={workerUrl || formAction || undefined}
        method="post"
        onSubmit={handleSubmit}
      >
        <label
          className="sr-only"
          htmlFor={compact ? "footer-email" : "hero-email"}
        >
          Work email
        </label>
        <input
          id={compact ? "footer-email" : "hero-email"}
          name="email"
          type="email"
          autoComplete="email"
          placeholder="Work email address"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <button type="submit">
          Join the list
          <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
        </button>
      </form>
      <p className="form-note" aria-live="polite">
        {message || "Occasional product updates. No noise."}
      </p>
    </div>
  );
}

function SignalMap() {
  return (
    <div
      className="signal-map"
      aria-label="Customer evidence becoming operational intelligence"
    >
      <div className="signal-topline">
        <span>Live understanding</span>
        <span className="status">
          <i /> Connected
        </span>
      </div>
      <div className="signal-stage">
        <div className="orbit orbit-one" />
        <div className="orbit orbit-two" />
        <div className="signal-source source-one">Cases</div>
        <div className="signal-source source-two">Calls</div>
        <div className="signal-source source-three">Email</div>
        <div className="signal-source source-four">Chat</div>
        <div className="signal-core">
          <span className="core-mark">
            <i />
            <i />
            <i />
          </span>
          <strong>Vox</strong>
          <small>Operational intelligence</small>
        </div>
        <svg className="signal-lines" viewBox="0 0 680 430" aria-hidden="true">
          <path d="M98 112 C210 126 220 190 300 215" />
          <path d="M584 92 C470 116 463 184 376 216" />
          <path d="M84 330 C207 320 224 260 301 232" />
          <path d="M598 333 C475 321 457 260 378 233" />
        </svg>
        <span className="pulse pulse-one" />
        <span className="pulse pulse-two" />
        <span className="pulse pulse-three" />
      </div>
      <div className="signal-footer">
        <span>
          <Check size={13} /> Permission-aware
        </span>
        <span>
          <Check size={13} /> Evidence-backed
        </span>
        <span>
          <Check size={13} /> Source-linked
        </span>
      </div>
    </div>
  );
}

const capabilityCards = [
  {
    number: "01",
    icon: Network,
    title: "See the whole operation",
    text: "Bring the signal from calls, cases, emails, chats and CRM activity into one continuously updated view.",
  },
  {
    number: "02",
    icon: Sparkles,
    title: "Understand what changed",
    text: "Detect emerging issues and changing customer needs before they become a lagging KPI or executive surprise.",
  },
  {
    number: "03",
    icon: FileSearch,
    title: "Know why it matters",
    text: "Connect patterns to products, processes and outcomes, with confidence and the evidence behind every conclusion.",
  },
  {
    number: "04",
    icon: CircleArrowOutUpRight,
    title: "Move from insight to action",
    text: "Give teams a clear next move, an accountable owner and a way to see whether the change worked.",
  },
];

function App() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div id="top">
      <header className="site-header">
        <div className="nav-shell">
          <Wordmark />
          <nav
            className={menuOpen ? "nav-links nav-links-open" : "nav-links"}
            aria-label="Main navigation"
          >
            <a href="#platform" onClick={() => setMenuOpen(false)}>
              Platform
            </a>
            <a href="#difference" onClick={() => setMenuOpen(false)}>
              Why Vox
            </a>
            <a href="#trust" onClick={() => setMenuOpen(false)}>
              Trust
            </a>
            <a className="mobile-demo" href={demoUrl}>
              View the demo <ArrowRight size={15} />
            </a>
          </nav>
          <div className="nav-actions">
            <a className="text-link" href={demoUrl}>
              View the demo <ArrowRight size={14} />
            </a>
            <a className="nav-cta" href="#join">
              Join the mailing list
            </a>
          </div>
          <button
            className="menu-button"
            type="button"
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X size={21} /> : <Menu size={21} />}
          </button>
        </div>
      </header>

      <main>
        <section className="hero section-shell">
          <div className="hero-copy">
            <p className="eyebrow">
              <span /> Customer operational intelligence
            </p>
            <h1>Know what your customers are telling you.</h1>
            <p className="hero-lede">
              Vox turns every customer interaction into a clear view of what is
              changing, why it matters and what your organisation should do
              next.
            </p>
            <MailingListForm />
            <a className="demo-link" href={demoUrl}>
              Explore the self-serve demo <ChevronRight size={16} />
            </a>
          </div>
          <div className="hero-visual">
            <SignalMap />
          </div>
        </section>

        <section className="context-strip" aria-label="Vox product context">
          <div className="section-shell context-inner">
            <p>Built for the customer-facing organisation</p>
            <div className="context-items">
              <span>Service</span>
              <i />
              <span>Experience</span>
              <i />
              <span>Product</span>
              <i />
              <span>Revenue</span>
              <i />
              <span>Operations</span>
            </div>
          </div>
        </section>

        <section className="problem section-shell" id="difference">
          <p className="section-kicker">The signal is already there</p>
          <div className="statement-grid">
            <h2>
              Your customers explain the operation every day. Most of it goes
              unheard.
            </h2>
            <div>
              <p>
                The answer is scattered across calls, messages, cases,
                complaints and CRM records. Traditional reporting sees a
                fraction. Surveys arrive late. Dashboards show what happened,
                but not why.
              </p>
              <p>
                Vox continuously turns that evidence into a living model of the
                customer experience, so teams can make better decisions while
                there is still time to act.
              </p>
            </div>
          </div>
        </section>

        <section className="platform-section" id="platform">
          <div className="section-shell">
            <div className="section-heading">
              <p className="section-kicker">From evidence to action</p>
              <h2>One intelligence layer for every customer interaction.</h2>
              <p>
                Not another dashboard. A governed understanding of what
                customers need and what the business should change.
              </p>
            </div>
            <div className="capability-grid">
              {capabilityCards.map(({ number, icon: Icon, title, text }) => (
                <article className="capability-card" key={number}>
                  <div className="card-topline">
                    <Icon size={20} strokeWidth={1.7} aria-hidden="true" />
                    <span>{number}</span>
                  </div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="dynamics section-shell">
          <div className="dynamics-copy">
            <p className="section-kicker">Start with Dynamics 365</p>
            <h2>Native context. No rip and replace.</h2>
            <p>
              Vox begins where your customer operation already lives. Connect
              Dynamics 365 to understand interactions in the context of cases,
              accounts, products and processes without replacing your CRM,
              contact centre or operating model.
            </p>
            <ul>
              <li>
                <Check size={16} /> Dynamics-native operational context
              </li>
              <li>
                <Check size={16} /> Original interactions remain in source systems
              </li>
              <li>
                <Check size={16} /> Designed to expand across channels
              </li>
            </ul>
          </div>
          <div
            className="context-model"
            aria-label="Dynamics records connected to Vox intelligence"
          >
            <div className="model-label">Operational context</div>
            <div className="model-rows">
              <span>
                <Database size={15} /> Cases
              </span>
              <span>
                <Database size={15} /> Accounts
              </span>
              <span>
                <Database size={15} /> Activities
              </span>
              <span>
                <Database size={15} /> Products
              </span>
            </div>
            <div className="model-connector">
              <i />
              <i />
              <i />
              <i />
            </div>
            <div className="model-output">
              <span className="model-output-mark">
                <i />
                <i />
                <i />
              </span>
              <div>
                <strong>Customer understanding</strong>
                <small>Linked to its operational cause</small>
              </div>
            </div>
          </div>
        </section>

        <section className="trust-section" id="trust">
          <div className="section-shell trust-grid">
            <div className="trust-copy">
              <p className="section-kicker light">
                Built for consequential decisions
              </p>
              <h2>Intelligence you can trust, trace and govern.</h2>
              <p>
                Your customer evidence is sensitive. Vox is designed
                around permission-aware access, explicit confidence and a
                verifiable path back to the source.
              </p>
              <a href={demoUrl}>
                See the trust model in the demo <ArrowRight size={15} />
              </a>
            </div>
            <div className="trust-list">
              <div>
                <ShieldCheck />
                <span>
                  <strong>Evidence-backed</strong>
                  <small>
                    Every conclusion retains provenance and confidence.
                  </small>
                </span>
              </div>
              <div>
                <LockKeyhole />
                <span>
                  <strong>Permission-aware</strong>
                  <small>
                    Evidence access respects both Vox and source-system
                    controls.
                  </small>
                </span>
              </div>
              <div>
                <Fingerprint />
                <span>
                  <strong>Tenant-isolated</strong>
                  <small>
                    Customer boundaries are enforced to protect your data
                  </small>
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="final-cta section-shell" id="join">
          <span className="final-mark" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <p className="section-kicker">Get closer to your customers</p>
          <h2>The next generation of customer intelligence is taking shape.</h2>
          <p>
            Join the mailing list for product updates, early access and
            practical thinking on customer operational intelligence.
          </p>
          <MailingListForm compact />
        </section>
      </main>

      <footer>
        <div className="section-shell footer-inner">
          <Wordmark />
          <p>Customer operational intelligence.</p>
          <div className="footer-links">
            <a href={demoUrl}>Demo</a>
            <a href="#top">Back to top ↑</a>
          </div>
          <small>© {new Date().getFullYear()} Vox. All rights reserved.</small>
        </div>
      </footer>
    </div>
  );
}

export { App };
